import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { Link as RouterLink } from "react-router-dom";
import logo from "../../assets/images/logo.png";

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const menuItems = [
    { label: "Home", path: "/", icon: <HomeIcon /> },
    { label: "About", path: "/about", icon: <InfoIcon /> },
    { label: "Contact", path: "/contact", icon: <ContactMailIcon /> },
    {
      label: "Apply Now",
      path: "/apply",
      primary: true,
      icon: <TouchAppIcon />,
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        background: "linear-gradient(135deg, #A8856B 0%, #FAF8F3 250%)",
        color: "white",
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
            fontFamily: '"Berlin Sans FB Demi", "Arial", sans-serif',
            fontSize: "1.4rem",
            color: "#FAF8F3",
            textDecoration: "none",
            letterSpacing: "0.03em",
          }}
        >
          MRLOANS
        </Typography>
        <IconButton onClick={handleDrawerToggle} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.2)" }} />
      <List sx={{ px: 1, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: 2,
                color: "white",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.15)",
                },
                ...(item.primary && {
                  background: "rgba(255, 255, 255, 0.2)",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.3)",
                  },
                }),
              }}
            >
              <Box sx={{ mr: 2, display: "flex", alignItems: "center" }}>
                {item.icon}
              </Box>
              <ListItemText
                primary={item.label}
                sx={{
                  "& .MuiTypography-root": {
                    fontWeight: item.primary ? 700 : 600,
                    fontSize: item.primary ? "1.1rem" : "1rem",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "white",
          color: "text.primary",
          borderBottom: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            <Box
              component="img"
              src={logo}
              alt="MRLOANS Logo"
              sx={{
                height: 50,
                width: 50,
                mr: 1,
                objectFit: "contain",
              }}
            />
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{
                flexGrow: 1,
                fontWeight: 500,
                fontFamily: '"Berlin Sans FB Demi", "Arial", sans-serif',
                fontSize: "1.4rem",
                color: "#5a5a5a",
                textDecoration: "none",
                letterSpacing: "0.03em",
                "&:hover": {
                  color: "#3a3a3a",
                },
              }}
            >
              MRLOANS
            </Typography>

            {/* Desktop Menu */}
            {!isMobile && (
              <Box sx={{ display: "flex", gap: 2 }}>
                {menuItems.map((item) =>
                  item.primary ? (
                    <Button
                      key={item.label}
                      component={RouterLink}
                      to={item.path}
                      variant="contained"
                      color="primary"
                    >
                      {item.label}
                    </Button>
                  ) : (
                    <Button
                      key={item.label}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: (theme) => theme.palette.primary.main,
                        fontWeight: 600,
                        "&:hover": {
                          color: (theme) => theme.palette.secondary.main,
                          background: "rgba(102, 126, 234, 0.08)",
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  )
                )}
              </Box>
            )}

            {/* Mobile Menu Icon */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{
                  color: (theme) => theme.palette.primary.main,
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            border: "none",
            borderRadius: 0,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
